<template>
  <section class="subscription-report" :aria-busy="loading">
    <div class="report-heading">
      <div>
        <span class="eyebrow">Planes profesionales</span>
        <h3>Suscripciones</h3>
        <p>Ingresos, continuidad y uso de los beneficios incluidos.</p>
      </div>
      <button type="button" class="refresh-button" :disabled="loading" @click="fetchReport">
        {{ loading ? 'Actualizando…' : 'Actualizar' }}
      </button>
    </div>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="loading" class="notice" role="status">Cargando estadísticas de suscripciones…</p>

    <template v-if="report && !loading && !error">
      <p class="period-caption">
        Período: {{ report.period.start_date }} al {{ report.period.end_date }} · Hora de Chile
        <span v-if="report.period.partial" class="pill">Día actual parcial</span>
      </p>
      <p v-if="!report.summary.paid_cycles && !report.summary.created_contracts" class="notice">
        No hay pagos ni nuevas contrataciones en este período. El estado actual se muestra igualmente.
      </p>

      <h4>Estado actual <small>Al {{ formatDate(report.period.generated_at) }}</small></h4>
      <div class="kpi-grid">
        <article v-for="card in currentCards" :key="card.label" class="kpi-card">
          <span>{{ card.label }}</span><strong>{{ card.value }}</strong><small>{{ card.detail }}</small>
        </article>
      </div>
      <p class="explanation">
        Vigentes = profesionales con una mensualidad pagada que cubre hoy. MRR = ingreso mensual recurrente
        de quienes mantienen renovación autorizada, usando el importe de su mensualidad vigente.
        Excluye a quienes cancelaron o pausaron la renovación.
      </p>

      <h4>Ingresos del período <small>Por fecha de pago · CLP</small></h4>
      <div class="kpi-grid">
        <article v-for="card in revenueCards" :key="card.label" class="kpi-card revenue-card">
          <span>{{ card.label }}</span><strong>{{ card.value }}</strong><small>{{ card.detail }}</small>
        </article>
      </div>

      <div class="report-section" v-if="report.points_transfer">
        <h4>Resultado tras puntos usados en solicitudes</h4>
        <div class="kpi-grid">
          <article class="kpi-card"><span>Ingresos netos completos</span><strong>{{ money(report.summary.net_clp) }}</strong><small>Cuotas pagadas en el período</small></article>
          <article class="kpi-card"><span>Imputación a adquisición</span><strong>−{{ money(report.summary.plan_transfer_net_clp) }}</strong><small>{{ number(report.points_transfer.net_points) }} puntos netos consumidos</small></article>
          <article class="kpi-card"><span>Contribución tras puntos</span><strong>{{ money(report.summary.contribution_after_points_clp) }}</strong><small>Antes de pasarela, IA, soporte y otros costos</small></article>
        </div>
        <p class="explanation">{{ report.points_transfer.description }}</p>
        <p class="explanation">La imputación se suma a adquisición por el mismo importe. Usa solicitudes creadas en el rango y consumos hasta hoy, aunque las cuotas se hayan pagado en otro período. Puede superar los ingresos del rango; no equivale a utilidad contable. Comparar ambos reportes al mismo corte y con igual cobertura de solicitudes.</p>
        <p v-if="report.summary.unvalued_plan_points" class="notice">{{ number(report.summary.unvalued_plan_points) }} puntos del plan sin precio histórico observable se valoraron en cero.</p>
      </div>

      <div class="report-section">
        <h4>Evolución diaria</h4>
        <p>Ingresos sin IVA y profesionales que pagaron su primera suscripción.</p>
        <line-chart v-if="report.daily.length" :chart-data="chartData" :options="chartOptions" :height="300" />
      </div>

      <div class="report-section">
        <h4>Comparación por plan</h4>
        <p>Vigencia y MRR a hoy; pagos e ingresos dentro del rango seleccionado. Un profesional puede pagar dos planes en el período.</p>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Plan</th><th>Vigentes hoy</th><th>En promoción</th><th>Renuevan</th><th>MRR sin IVA</th><th>Pagadores</th><th>Cuotas pagadas</th><th>Ingresos sin IVA</th><th>IVA</th><th>Imputación a adquisición</th><th>Contribución tras puntos</th></tr></thead>
            <tbody><tr v-for="plan in report.plans" :key="plan.plan">
              <th>{{ planName(plan.plan) }}</th><td>{{ number(plan.active) }}</td><td>{{ number(plan.promotional) }}</td>
              <td>{{ number(plan.renewing) }}</td><td>{{ money(plan.mrr_net_clp) }}</td><td>{{ number(plan.payers) }}</td>
              <td>{{ number(plan.paid_cycles) }}</td><td>{{ money(plan.net_clp) }}</td><td>{{ money(plan.vat_clp) }}</td>
              <td>{{ money(plan.plan_transfer_net_clp) }}</td><td>{{ money(plan.contribution_after_points_clp) }}</td>
            </tr></tbody>
          </table>
        </div>
      </div>

      <div class="section-grid">
        <article v-for="group in metricGroups" :key="group.title" class="report-section">
          <h4>{{ group.title }}</h4><p>{{ group.description }}</p>
          <dl class="metric-list">
            <div v-for="metric in group.metrics" :key="metric.label"><dt>{{ metric.label }}</dt><dd>{{ metric.value }}</dd></div>
          </dl>
        </article>
      </div>

      <div class="report-section">
        <h4>Puntos incluidos en el plan</h4>
        <p>Lotes acreditados en el período, con consumo y remanente observados hasta hoy. Se descuentan las restituciones.</p>
        <div class="kpi-grid points-grid">
          <article class="kpi-card"><span>Puntos acreditados</span><strong>{{ number(report.points.granted) }}</strong><small>{{ number(report.points.grants) }} lotes · {{ number(report.points.professionals) }} profesionales</small></article>
          <article class="kpi-card"><span>Consumo neto de esos lotes</span><strong>{{ number(report.points.used_net) }}</strong><small>{{ percent(report.points.utilization_rate) }} de los puntos acreditados</small></article>
          <article class="kpi-card"><span>Saldo de todos los planes hoy</span><strong>{{ number(report.points.current_balance) }}</strong><small>Incluye lotes de otros períodos</small></article>
          <article class="kpi-card"><span>Por vencer en 7 días</span><strong>{{ number(report.points.expiring_7d) }}</strong><small>Del saldo disponible hoy</small></article>
        </div>
        <dl class="metric-list point-balances">
          <div><dt>Remanente disponible de los lotes del período</dt><dd>{{ number(report.points.available) }}</dd></div>
          <div><dt>Remanente vencido sin usar</dt><dd>{{ number(report.points.expired) }}</dd></div>
          <div><dt>Remanente revocado por reversión</dt><dd>{{ number(report.points.revoked) }}</dd></div>
          <div><dt>Remanente de vigencia futura</dt><dd>{{ number(report.points.future) }}</dd></div>
        </dl>
        <p class="notice">Los puntos incluidos no son una venta adicional. Su valor asignado se suma a adquisición y se descuenta de la contribución de suscripciones, conservando los ingresos completos de las cuotas.</p>
      </div>

      <div class="report-section">
        <div class="report-heading">
          <div><h4>Seguimiento operativo actual</h4><p>Casos pendientes a hoy, independientemente del rango de fechas.</p></div>
          <router-link class="management-link" to="/superadmin/SuscripcionesProfesionales">Gestionar suscripciones →</router-link>
        </div>
        <dl class="metric-list operations-grid">
          <div v-for="metric in operations" :key="metric.label"><dt>{{ metric.label }}</dt><dd :class="{ attention: metric.value > 0 }">{{ number(metric.value) }}</dd></div>
        </dl>
        <div class="status-list"><span v-for="(count, status) in report.current.contract_statuses" :key="status" class="pill">{{ statusName(status) }}: {{ number(count) }}</span></div>
        <p class="explanation">Estados de todos los contratos históricos; una autorización de cobro no demuestra una mensualidad pagada.</p>
      </div>

      <details class="report-section methodology">
        <summary>Cómo se calculan estas cifras</summary>
        <ul><li v-for="note in report.notes" :key="note">{{ note }}</li></ul>
      </details>
    </template>
  </section>
</template>

<script>
import axios from 'axios';
import LineChart from '@/components/charts/LineChart.js';

export default {
  name: 'SubscriptionReport',
  components: { LineChart },
  props: { startDate: { type: String, required: true }, endDate: { type: String, required: true } },
  data: () => ({ report: null, loading: false, error: null, requestSerial: 0, reloadTimer: null }),
  computed: {
    currentCards() {
      const c = this.report.current;
      return [
        { label: 'Profesionales vigentes', value: this.number(c.active), detail: `${this.number(c.promotional)} en precio promocional` },
        { label: 'Con renovación activa', value: this.number(c.renewing), detail: `${this.number(c.non_renewing)} vigentes sin renovación activa` },
        { label: 'MRR neto sin IVA', value: this.money(c.mrr_net_clp), detail: `${this.money(c.mrr_gross_clp)} con IVA` },
        { label: 'MRR a tarifa normal sin IVA', value: this.money(c.regular_mrr_net_clp), detail: 'Escenario sin promoción, según oferta contratada' },
      ];
    },
    revenueCards() {
      const s = this.report.summary;
      return [
        { label: 'Ingresos netos sin IVA', value: this.money(s.net_clp), detail: `IVA: ${this.money(s.vat_clp)}` },
        { label: 'Pagos válidos con IVA', value: this.money(s.gross_clp), detail: 'Excluye cuotas revertidas' },
        { label: 'Profesionales que pagaron', value: this.number(s.payers), detail: `${this.number(s.paid_cycles)} mensualidades pagadas` },
        { label: 'Ingreso por pagador sin IVA', value: this.money(s.revenue_per_payer_net_clp), detail: 'Ingreso del período / pagadores únicos' },
      ];
    },
    metricGroups() {
      const s = this.report.summary;
      const a = this.report.activity;
      const item = (label, value, type = 'number') => ({ label, value: this[type](value) });
      return [
        { title: 'Adopción y conversión', description: 'Contrataciones iniciadas y primeros pagos en el período.', metrics: [
          item('Contrataciones iniciadas', s.created_contracts), item('De esas, con pago al cierre', s.converted_contracts),
          item('Conversión a primer pago', s.checkout_conversion, 'percent'), item('Nuevos profesionales pagadores', s.new_payers),
          item('Mensualidades promocionales pagadas', s.promotional_payments), item('Pagos de renovación', s.renewal_payments),
        ] },
        { title: 'Continuidad y bajas', description: 'La retención compara la misma base de profesionales; cambiar de plan conserva la continuidad.', metrics: [
          item('Vigentes al inicio → al cierre', `${s.opening_active} → ${s.closing_active}`, 'identity'),
          item('Variación neta de vigentes', s.net_growth), item('Bajas de la base inicial', s.lost_opening),
          item('Retención de la base inicial', s.retention_rate, 'percent'), item('Tasa de bajas (churn)', s.churn_rate, 'percent'),
          item('Renovaciones pagadas / vencimientos', `${s.renewals_paid} / ${s.renewals_due}`, 'identity'),
          item('Tasa de renovación continua', s.renewal_rate, 'percent'), item('Cancelaciones confirmadas', s.cancellations),
        ] },
        { title: 'Salud de cobros', description: 'Cuotas cuyo período empieza en el rango, con su estado conciliado actual.', metrics: [
          item('Cuotas programadas', s.billing_cycles), item('Cuotas pagadas', s.billing_statuses.paid || 0),
          item('Cuotas pendientes', s.billing_statuses.pending || 0), item('Cuotas rechazadas', s.billing_statuses.rejected || 0),
          item('Cuotas revertidas', s.billing_statuses.reversed || 0), item('Tasa de cobro', s.collection_rate, 'percent'),
          item('Pagos del rango luego revertidos', s.reversed_cycles),
          item('Importe original excluido por reversión', s.reversed_original_gross_clp, 'money'),
        ] },
        { title: 'Uso de beneficios', description: 'Actividad del período mientras existía una mensualidad pagada vigente.', metrics: [
          item('Generaciones y modificaciones con IA', a.ai_interactions), item('Profesionales que generaron con IA', a.ai_professionals),
          ...(a.ai_unclassified_interactions > 0
            ? [item('Usos históricos sin clasificar (excluidos)', a.ai_unclassified_interactions)] : []),
          item('Vistas de perfiles', a.profile_views), item('Clics en WhatsApp', a.whatsapp_clicks),
          item('Clics a sitio web o Instagram', a.website_clicks || 0),
          item('Ingreso mensual por renovante sin IVA', this.report.current.arpu_net_clp, 'money'),
          item('Vigencias que terminan en próximos 7 días', this.report.current.ending_7d),
        ] },
      ];
    },
    operations() {
      const c = this.report.current;
      return [
        ['Contratos que requieren revisión', c.review_required], ['Contratos con error de sincronización', c.sync_errors],
        ['Cancelaciones pendientes', c.cancel_pending], ['Cuotas pagadas sin folio registrado', c.pending_documents],
        ['Confirmaciones de pago pendientes', c.pending_emails], ['Eventos pendientes de procesar', c.pending_events],
        ['Eventos pendientes con error', c.failed_events],
      ].map(([label, value]) => ({ label, value }));
    },
    chartData() {
      return { labels: this.report.daily.map(row => row.date), datasets: [
        { label: 'Ingresos sin IVA', data: this.report.daily.map(row => row.net_clp), borderColor: '#176b84', backgroundColor: 'rgba(23,107,132,.08)', fill: true, lineTension: 0.15, yAxisID: 'income' },
        { label: 'Nuevos pagadores', data: this.report.daily.map(row => row.new_payers), borderColor: '#b66e17', fill: false, lineTension: 0.15, yAxisID: 'people' },
      ] };
    },
    chartOptions() {
      return { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' },
        tooltips: { mode: 'index', intersect: false },
        scales: { xAxes: [{ ticks: { maxTicksLimit: 12 } }], yAxes: [
          { id: 'income', position: 'left', ticks: { beginAtZero: true, callback: value => this.money(value) } },
          { id: 'people', position: 'right', ticks: { beginAtZero: true, precision: 0 }, gridLines: { drawOnChartArea: false } },
        ] } };
    },
  },
  watch: { startDate: 'scheduleFetch', endDate: 'scheduleFetch' },
  mounted() { this.fetchReport(); },
  beforeDestroy() { clearTimeout(this.reloadTimer); this.requestSerial += 1; },
  methods: {
    scheduleFetch() {
      this.requestSerial += 1;
      this.report = null;
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.fetchReport(), 120);
    },
    async fetchReport() {
      const serial = ++this.requestSerial;
      this.report = null;
      this.error = null;
      if (!this.startDate || !this.endDate || this.startDate > this.endDate) {
        this.loading = false;
        this.error = 'Selecciona un rango de fechas válido.';
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`${window.hostname || ''}api/admin/subscription_statistics`, {
          params: { start_date: this.startDate, end_date: this.endDate },
        });
        if (serial === this.requestSerial) this.report = response.data;
      } catch (error) {
        if (serial === this.requestSerial) this.error = error.response?.data?.error || 'No se pudieron cargar las estadísticas. Intenta actualizar.';
      } finally {
        if (serial === this.requestSerial) this.loading = false;
      }
    },
    identity(value) { return value; },
    number(value) { return value == null ? '—' : new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(value); },
    money(value) { return value == null ? '—' : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value); },
    percent(value) { return value == null ? '—' : new Intl.NumberFormat('es-CL', { style: 'percent', maximumFractionDigits: 1 }).format(value); },
    formatDate(value) { return new Date(value).toLocaleString('es-CL', { timeZone: 'America/Santiago' }); },
    planName(code) { return ({ pro: 'Empresa Pro', activa: 'Empresa Activa', gratis: 'Gratis' })[code] || code; },
    statusName(status) { return ({ creating: 'En creación', pending: 'Esperando autorización', authorized: 'Autorizados', paused: 'Pausados', cancelled: 'Cancelados', creation_failed: 'Creación fallida' })[status] || status; },
  },
};
</script>

<style scoped>
.subscription-report { color: #243746; }
.report-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.eyebrow { color: #176b84; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; font-weight: 700; }
h3 { font-size: 25px; margin: 4px 0 8px; }
h4 { font-size: 17px; margin: 0 0 14px; font-weight: 650; }
h4 small { color: #637785; font-size: 12px; margin-left: 10px; font-weight: 400; }
p { color: #637785; font-size: 13px; line-height: 1.6; margin: 0 0 16px; }
.refresh-button, .management-link { border: 1px solid #c4d6df; border-radius: 7px; padding: 9px 14px; background: white; color: #176b84; font-size: 13px; }
.refresh-button { cursor: pointer; }
.refresh-button:disabled { opacity: .6; cursor: wait; }
.period-caption { margin: 12px 0 24px; }
.pill { display: inline-block; background: #edf3f6; border-radius: 20px; padding: 5px 10px; font-size: 12px; color: #405765; }
.kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
.kpi-card { background: #fff; border: 1px solid #dce6ec; border-radius: 10px; padding: 19px; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.kpi-card span { color: #526a79; font-size: 13px; }
.kpi-card strong { color: #176b84; font-size: 27px; font-weight: 700; overflow-wrap: anywhere; }
.kpi-card small { font-size: 12px; color: #70828e; line-height: 1.5; }
.revenue-card { border-top: 3px solid #176b84; }
.explanation { font-size: 12px; margin: 14px 0 26px; }
.report-section { background: white; border: 1px solid #dce6ec; border-radius: 10px; padding: 22px; margin-top: 22px; min-width: 0; }
.section-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 13px; white-space: nowrap; }
th, td { padding: 13px 12px; text-align: right; border-bottom: 1px solid #e7edf1; }
th:first-child { text-align: left; }
thead th { background: #f5f8fa; color: #567080; font-size: 12px; }
.metric-list { margin: 0; }
.metric-list div { display: flex; justify-content: space-between; gap: 20px; padding: 11px 0; border-bottom: 1px solid #edf1f4; font-size: 13px; }
dt { color: #526a79; font-weight: 400; }
dd { margin: 0; font-weight: 650; white-space: nowrap; }
.points-grid { margin: 20px 0; }
.point-balances { max-width: 720px; }
.notice { background: #edf6f8; border: 1px solid #d3e9ee; border-radius: 8px; padding: 14px 18px; margin: 18px 0; color: #315c6a; }
.error { background: #fff0ed; border-color: #f3c9c0; color: #a53d27; }
.operations-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 32px; }
.attention { color: #ac6515; }
.status-list { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 20px; }
.methodology summary { cursor: pointer; font-weight: 650; }
.methodology ul { padding-left: 20px; margin: 18px 0 0; color: #637785; font-size: 13px; line-height: 1.7; }
.methodology li + li { margin-top: 8px; }
@media (max-width: 1200px) { .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) { .section-grid, .operations-grid { grid-template-columns: 1fr; gap: 0; } .report-section { padding: 16px; } .kpi-card { padding: 14px; } .kpi-card strong { font-size: 23px; } h4 small { display: block; margin: 6px 0 0; } }
@media (max-width: 400px) { .kpi-grid { grid-template-columns: 1fr; } }
</style>
